import React from 'react';
import { AnimatedSection } from '../components/animated/AnimatedSection';
import { MagneticButton } from '../components/animated/MagneticButton';
import { ContinuousImageGallery } from '../components/animated/ContinuousImageGallery';
import { useScrollReveal } from '../hooks/useGSAP';
import { useNavigate } from 'react-router-dom';
import { Heart } from 'lucide-react';
export function YouthEngagementPage() {
  const heroRef = useScrollReveal();
  const navigate = useNavigate();
  const galleryImagesRow1 = [{
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7TvpauaDivvL5G4gnDiQzf/DSC_0231.jpg',
    alt: 'Youth Training Session'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/2r374TFUK98iYF6vZh8KaX/DSC_0278.jpg',
    alt: 'Professional Development Session'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/qTR2P2Abp2xV7H4uMBtB7r/1755202594528.jpg',
    alt: 'Youth Empowerment Training'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gc46pSLNc5V92oWNiseBjn/IMG_6586.jpg',
    alt: 'SkillzHQ Panel Discussion'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9FZKDGZQqf4JiekCQp6n7L/IMG_6037.jpg',
    alt: 'Panel Discussion Event'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ugttQfWmUv3yN5nuNY9y5K/IMG_7014.png',
    alt: 'SkillzHQ Team Member'
  }];
  const galleryImagesRow2 = [{
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6gqzsBtULohZaqZkyXnupX/DSC_0432.jpg',
    alt: 'SkillzHQ Community Impact'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/umCgrBTNLtDWCBxu8jbtu2/IMG_6745.jpg',
    alt: 'Youth Training Group Photo'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/wL8vYnsQQ75eP4k4Fhi6A4/IMG_6747.jpg',
    alt: 'SkillzHQ Team Group Photo'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ckN7GyibvNyHAS1TcJgxJa/IMG_6749.jpg',
    alt: 'Leadership Team Photo'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nT3kVm3q7omcRMbTWY453P/IMG-20250331-WA0007.jpg',
    alt: 'Youth Training Workshop'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/sCfUTDcb5msfea8tsYVG4n/JOOPSA_-_Operationalising_the_John_Odigie-Oyegun_Public_service_Academy.jpg',
    alt: 'JOOPSA Graduation Ceremony'
  }];
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-1654">
      <main className="flex-grow pt-20" data-id="element-1655">
        {/* Hero Section */}
        <section className="relative py-32 overflow-hidden" data-id="element-1656">
          <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-white to-blue-50" data-id="element-1657" />
          <div className="absolute top-20 right-10 w-96 h-96 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20" data-id="element-1658" />

          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-1659">
            <div className="max-w-4xl mx-auto text-center" ref={heroRef as React.RefObject<HTMLDivElement>} data-id="element-1660">
              <div className="inline-block mb-6" data-id="element-1661">
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-green-50 text-green-700 text-sm font-medium border border-green-100" data-id="element-1662">
                  <Heart className="w-4 h-4 mr-2" data-id="element-1663" />
                  Empowering Africa's Future
                </span>
              </div>

              <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-gray-900 mb-8" data-id="element-1664">
                Youth Engagement
                <br data-id="element-1665" />
                <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent" data-id="element-1666">
                  & Development
                </span>
              </h1>

              <p className="text-xl text-gray-600 leading-relaxed mb-8" data-id="element-1667">
                With over 60% of Africa's population under 25, we're committed
                to equipping young Africans with the skills, confidence, and
                opportunities they need to thrive in the modern economy.
              </p>
            </div>
          </div>
        </section>

        {/* Mission Statement */}
        <AnimatedSection className="py-24 bg-white" data-id="element-1668">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1669">
            <div className="max-w-4xl mx-auto" data-id="element-1670">
              <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center" data-id="element-1671">
                Our Youth Mission
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6" data-id="element-1672">
                At SkillzHQ, we believe that investing in youth is investing in
                Africa's future. Our youth engagement programs are designed to
                bridge the gap between education and employment, providing
                practical skills, mentorship, and pathways to meaningful
                careers.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed" data-id="element-1673">
                Through partnerships with government agencies, NGOs, and private
                sector organizations, we've trained over 100,000 young people
                across Africa through our trainings and programs, helping them
                secure dignified employment and launch successful
                entrepreneurial ventures.
              </p>
            </div>
          </div>
        </AnimatedSection>

        {/* Continuous Sliding Photo Gallery */}
        <AnimatedSection className="py-24 bg-gradient-to-b from-gray-50 to-white" data-id="element-1674">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1675">
            <div className="text-center mb-16" data-id="element-1676">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-1677">
                Our Impact in Action
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1678">
                Real stories, real transformation — see how we're empowering
                Africa's youth
              </p>
            </div>

            <div className="space-y-6" data-id="element-1679">
              {/* First row - sliding right */}
              <ContinuousImageGallery images={galleryImagesRow1} direction="right" speed={0.5} data-id="element-1680" />

              {/* Second row - sliding left */}
              <ContinuousImageGallery images={galleryImagesRow2} direction="left" speed={0.5} data-id="element-1681" />
            </div>
          </div>
        </AnimatedSection>

        {/* Impact Stats */}
        <AnimatedSection className="py-24 bg-white" data-id="element-1682">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1683">
            <div className="max-w-6xl mx-auto" data-id="element-1684">
              <div className="text-center mb-16" data-id="element-1685">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-1686">
                  Our Youth Impact
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1687">
                <div className="text-center p-8 bg-gradient-to-br from-green-50 to-white rounded-2xl shadow-lg border-2 border-green-100" data-id="element-1688">
                  <div className="text-5xl font-bold text-green-600 mb-4" data-id="element-1689">
                    100,000+
                  </div>
                  <p className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1690">
                    Young People Trained
                  </p>
                  <p className="text-gray-600" data-id="element-1691">
                    Across Africa through our trainings and programs
                  </p>
                </div>

                <div className="text-center p-8 bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-lg border-2 border-blue-100" data-id="element-1692">
                  <div className="text-5xl font-bold text-blue-600 mb-4" data-id="element-1693">
                    43,800
                  </div>
                  <p className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1694">
                    Training Hours
                  </p>
                  <p className="text-gray-600" data-id="element-1695">
                    Equivalent of 5 years of continuous learning
                  </p>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* CTA Section */}
        <AnimatedSection className="py-32 bg-gradient-to-br from-green-600 to-blue-600 relative overflow-hidden" data-id="element-1696">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]" data-id="element-1697" />
          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-1698">
            <div className="max-w-4xl mx-auto text-center" data-id="element-1699">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" data-id="element-1700">
                Join Us in Empowering Africa's Youth
              </h2>
              <p className="text-xl text-green-100 mb-12" data-id="element-1701">
                Partner with us to create opportunities and transform lives
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1702">
                <MagneticButton variant="secondary" size="lg" onClick={() => navigate('/contact')} data-id="element-1703">
                  Partner With Us
                </MagneticButton>
                <MagneticButton variant="outline" size="lg" onClick={() => navigate('/courses')} data-id="element-1704">
                  View Programs
                </MagneticButton>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </main>
    </div>;
}